import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { atxIsDesktop$, atxIsMobile$ } from '@epicuro-next/ataraxia/breakpoint';
import {
  configStripConfigurationDefaultValues,
  ConfigStripConfigurationKeys,
  ConfigStripConfigurations,
  CONFIG_STRIP_FEATURE,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import {
  DisplayByMode,
  selectAdvancedMdo,
  selectAvailableVariants,
  selectDisplayByMode,
  selectHasPrepacksForCurrentDeliveryPeriod,
  selectShowAMDO,
  setDisplayByMode,
} from '@epicuro-next/state/config-strip-state';
import {
  DabConfigurationKeys,
  DabStateConfigurations,
  DAB_FEATURE_NAME,
} from '@epicuro-next/state/dab-state';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, shareReplay, takeUntil } from 'rxjs/operators';

import { ConfigStripUiStore } from '../../store/config-strip-ui.store';
import { InboundDetailsComponentStore } from '../inbound-details/inbound-details.component-store';

import {
  activeMessage,
  disabledMessage,
  inactiveMessage,
} from './switcher-tooltip.labels';

@Component({
  selector: 'epc-grid-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './grid-header.component.html',
  styleUrls: ['./grid-header.component.scss'],
  providers: [InboundDetailsComponentStore],
  standalone: false,
})
export class GridHeaderComponent implements OnInit, OnDestroy {
  @Input() set compactMdo(value: boolean) {
    this.compactControl.setValue(value, { emitEvent: false });
  }

  @Output() private compactModeToggle = new EventEmitter();

  private readonly pageStore = inject(ConfigStripUiStore);

  public readonly openInboundDetails$$ = this.pageStore.isOpenInboundDetails$;

  constructor(
    private store: Store,
    private inboundDetailsComponentStore: InboundDetailsComponentStore,
  ) {}

  public DisplayByMode = DisplayByMode;
  private destroy$ = new Subject<void>();
  public compactControl = new UntypedFormControl(false);

  public readonly showInboundDetailsLink$ = combineLatest([
    this.store.pipe(
      select(
        selectConfigurationValueFrom<
          DabConfigurationKeys.InboundDetailsTurnedOn,
          DabStateConfigurations
        >(DAB_FEATURE_NAME, DabConfigurationKeys.InboundDetailsTurnedOn, false),
      ),
    ),
    this.inboundDetailsComponentStore.hasItems$,
  ]).pipe(map(([on, hasItems]) => on && hasItems));

  public readonly showToggle$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        ConfigStripConfigurationKeys.ShowDisplayModeToggle,
        ConfigStripConfigurations
      >(
        CONFIG_STRIP_FEATURE,
        ConfigStripConfigurationKeys.ShowDisplayModeToggle,
        configStripConfigurationDefaultValues[
          ConfigStripConfigurationKeys.ShowDisplayModeToggle
        ],
      ),
    ),
  );

  public readonly hasLooseSizes$ = this.store.pipe(
    select(selectAvailableVariants),
    map((variants) => !!variants?.length),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public readonly hasPrepacks$ = this.store.pipe(
    select(selectHasPrepacksForCurrentDeliveryPeriod),
  );

  public readonly selectedDisplayByMode$ = this.store.pipe(
    select(selectDisplayByMode),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public isAdvancedOrPrepack$ = this.selectedDisplayByMode$.pipe(
    map(
      (mode) =>
        mode === DisplayByMode.Prepack ||
        mode === DisplayByMode.AdvancedPrepack ||
        mode === DisplayByMode.AdvancedSize,
    ),
  );

  public readonly showLegend$ = combineLatest([
    this.store.pipe(
      select(
        selectConfigurationValueFrom<
          ConfigStripConfigurationKeys.ShowGridLegend,
          ConfigStripConfigurations
        >(
          CONFIG_STRIP_FEATURE,
          ConfigStripConfigurationKeys.ShowGridLegend,
          configStripConfigurationDefaultValues[
            ConfigStripConfigurationKeys.ShowGridLegend
          ],
        ),
      ),
    ),
    atxIsDesktop$,
    this.isAdvancedOrPrepack$,
  ]).pipe(
    map(
      ([on, isDesktop, isAdvancedOrPrepack]) =>
        on && isDesktop && !isAdvancedOrPrepack,
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public showAmdo$: Observable<boolean> = combineLatest([
    atxIsMobile$,
    this.store.pipe(select(selectShowAMDO)),
  ]).pipe(
    map(([isMobile, showMDO]) => !isMobile && showMDO),
    shareReplay({ bufferSize: 1, refCount: true }),
    takeUntil(this.destroy$),
  );

  public readonly showGridHeader$ = combineLatest([
    this.showLegend$,
    this.showToggle$,
    this.showAmdo$,
    this.showInboundDetailsLink$,
  ]).pipe(
    map(
      ([
        showLegend,
        showDisplayModeToggle,
        showAmdo,
        showInboundDetailsLink,
      ]) => {
        return Boolean(
          showLegend ||
            showDisplayModeToggle ||
            showAmdo ||
            showInboundDetailsLink,
        );
      },
    ),
  );

  public readonly selectedAdvancedMdoEnabled$ = combineLatest([
    this.store.pipe(select(selectAdvancedMdo)),
    atxIsMobile$,
  ]).pipe(map(([isAmdo, isMobile]) => isAmdo && !isMobile));

  public switcherTooltip$ = combineLatest([
    this.selectedDisplayByMode$,
    this.hasLooseSizes$,
    this.selectedAdvancedMdoEnabled$,
  ]).pipe(
    map(([displayByMode, showSizes, amdoActive]) => {
      if (displayByMode === DisplayByMode.Size && showSizes) {
        return disabledMessage;
      }
      if (amdoActive) {
        return activeMessage;
      }

      return inactiveMessage;
    }),
  );

  public ngOnInit() {
    this.compactControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.compactModeToggle.emit(value));
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public changeDisplayByMode(displayMode: DisplayByMode) {
    this.store.dispatch(setDisplayByMode({ displayByMode: displayMode }));
  }

  public setVisibilityOfInboundDetails(value: boolean): void {
    this.pageStore.setInboundDetailsVisibility(value);
  }
}
